import {
  CButton,
  CCol,
  CInput,
  CLabel,
  CRow,
  CSelect,
  CTextarea,
} from "@coreui/react";
import React, { useCallback, useEffect, useState } from "react";
import Errors, { errorsAreSame, getFieldErrors } from "../../models/errors";
import CurrencyField from "../currencies/CurrencyField";
import { FieldErrors } from "../form/FieldErrors";
import {
  emptyValueOnUndefined,
  emptyValueOnUndefinedOrNull,
} from "../../utils/fields";
import {
  IVA_TYPE_10_PERCENT,
  IVA_TYPE_5_PERCENT,
  IVA_TYPE_CHOICES,
  IVA_TYPE_EXEMPT,
  IVA_TYPE_MIXED_10_PERCENT,
  IVA_TYPE_MIXED_5_PERCENT,
} from "../../models/iva";
import { CreditNoteItem } from "../../models/credit-note";
import Client, { CLIENT_TYPE_GOVERNMENT } from "../../models/client";
import CatalogItem, { MEASURE_UNIT_UNIT } from "../../models/catalog-item";
import { PYG } from "../../currency/available-currencies";
import { CREDIT_NOTES_CREATE, CREDIT_NOTES_UPDATE } from "../../auth/permissions";
import CatalogItemPicker from "../catalog-item/CatalogItemPicker";
import MeasureUnitSelect from "../measure-unit/MeasureUnitSelect";

const addTotalAmount = (
  item: CreditNoteItem,
  unitAmount?: number,
  quantity?: number,
): CreditNoteItem => {
  if (unitAmount !== undefined && quantity !== undefined) {
    item.totalAmount = unitAmount * quantity;
  }

  return item;
};

const addAmountValues = (item: CreditNoteItem) => {
  const newItem = { ...item };

  newItem.amount10Percent = undefined;
  newItem.amount5Percent = undefined;
  newItem.amountExempt = undefined;
  newItem.taxed10Percent = undefined;
  newItem.taxed5Percent = undefined;
  newItem.iva10Percent = undefined;
  newItem.iva5Percent = undefined;

  if (newItem.totalAmount !== undefined) {
    if (newItem.ivaType === IVA_TYPE_10_PERCENT) {
      newItem.amount10Percent = newItem.totalAmount;
      newItem.taxed10Percent = Math.round(newItem.totalAmount / (1 + 0.1));
      newItem.iva10Percent = Math.round(
        (newItem.totalAmount / (1 + 0.1)) * 0.1,
      );
    }

    if (newItem.ivaType === IVA_TYPE_5_PERCENT) {
      newItem.amount5Percent = newItem.totalAmount;
      newItem.taxed5Percent = Math.round(newItem.totalAmount / (1 + 0.05));
      newItem.iva5Percent = Math.round(
        (newItem.totalAmount / (1 + 0.05)) * 0.05,
      );
    }

    if (newItem.ivaType === IVA_TYPE_EXEMPT) {
      newItem.amountExempt = newItem.totalAmount;
    }
  }

  return newItem;
};

const addMissingValues = (item: CreditNoteItem) => {
  const newItem = { ...item };
  newItem.measureUnit = item.measureUnit ?? MEASURE_UNIT_UNIT;

  return newItem;
};

interface CreditNoteItemFormProps {
  item: CreditNoteItem;
  client: Client | null;
  currency: string;
  onDelete: (_: CreditNoteItem) => void;
  onChange: (_: CreditNoteItem) => void;
  errors: Errors;
}

const CreditNoteItemForm: React.FC<CreditNoteItemFormProps> = ({
  item,
  client,
  currency,
  onDelete,
  onChange,
  errors,
}) => {
  const [editingItem, setEditingItem] = useState(item);
  const [catalogItem, setCatalogItem] = useState<CatalogItem | null>(null);

  const onCatalogItemChange = (entry: CatalogItem | null) => {
    setCatalogItem(entry);
    if (entry === null) return;

    let newItem = { ...editingItem };
    newItem.description = entry.description ?? newItem.description;
    if (entry.code) newItem.code = entry.code;
    if (entry.ivaType) {
      newItem.ivaType = entry.ivaType;
      newItem.ivaBase =
        entry.ivaType === IVA_TYPE_10_PERCENT || entry.ivaType === IVA_TYPE_5_PERCENT
          ? 100
          : entry.ivaType === IVA_TYPE_EXEMPT
            ? 0
            : undefined;
    }
    newItem.measureUnit = entry.measureUnit ?? MEASURE_UNIT_UNIT;
    newItem.quantity = newItem.quantity ?? 1;
    if (entry.unitPrice != null && (entry.priceCurrency ?? PYG) === currency) {
      newItem.unitAmount = Number(entry.unitPrice);
      newItem.totalAmount = newItem.unitAmount * newItem.quantity;
    }
    newItem = addAmountValues(addMissingValues(newItem));
    setEditingItem(newItem);
    onChange(newItem);
  };

  const clearCatalogItem = () => {
    if (catalogItem !== null) setCatalogItem(null);
  };
  const onDeleteClick = useCallback(() => {
    onDelete(editingItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem]);

  const onQuantityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newItem = { ...editingItem };
      if (!isNaN(parseFloat(e.target.value))) {
        newItem.quantity = parseFloat(e.target.value);
        newItem = addTotalAmount(
          newItem,
          newItem.unitAmount,
          parseFloat(e.target.value),
        );
      } else {
        newItem.quantity = undefined;
        newItem = addTotalAmount(newItem, newItem.unitAmount, undefined);
      }

      newItem = addAmountValues(newItem);
      newItem = addMissingValues(newItem);

      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem],
  );

  const onCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem = { ...editingItem };
      newItem.code = e.target.value;
      setEditingItem(newItem);
      onChange(newItem);
      clearCatalogItem();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem, catalogItem],
  );

  const onMeasureUnitChange = (code: number) => {
    const newItem = { ...editingItem, measureUnit: code };
    setEditingItem(newItem);
    onChange(newItem);
  };

  const onDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem = { ...editingItem };
      newItem.description = e.target.value;
      setEditingItem(newItem);
      onChange(newItem);
      clearCatalogItem();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem, catalogItem],
  );

  const onDncpLevelCodeGeneralChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem = { ...editingItem };
      newItem.dncpLevelCodeGeneral = e.target.value;
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem],
  );

  const onDncpLevelCodeSpecificChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem = { ...editingItem };
      newItem.dncpLevelCodeSpecific = e.target.value;
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem],
  );

  const onUnitAmountChange = useCallback(
    (newUnitAmount?: number) => {
      let newItem = { ...editingItem };
      newItem.unitAmount = newUnitAmount;
      newItem = addTotalAmount(newItem, newUnitAmount, newItem.quantity);
      newItem = addAmountValues(newItem);
      newItem = addMissingValues(newItem);

      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem],
  );

  const onIvaTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newItem = { ...editingItem };
      newItem.ivaType = e.target.value;

      if (
        newItem.ivaType === IVA_TYPE_10_PERCENT ||
        newItem.ivaType === IVA_TYPE_5_PERCENT
      ) {
        newItem.ivaBase = 100;
      } else if (newItem.ivaType === IVA_TYPE_EXEMPT) {
        newItem.ivaBase = 0;
      } else {
        newItem.ivaBase = undefined;
      }

      newItem = addAmountValues(newItem);
      newItem = addMissingValues(newItem);

      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem],
  );

  const onIvaBaseChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newItem = { ...editingItem };

      if (!isNaN(parseInt(e.target.value))) {
        newItem.ivaBase = parseInt(e.target.value);
      } else {
        newItem.ivaBase = undefined;
      }

      newItem = addAmountValues(newItem);
      newItem = addMissingValues(newItem);

      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem],
  );

  useEffect(() => {
    setEditingItem(item);
  }, [item]);

  const itemLabelStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: "var(--theme-muted)",
    marginBottom: "3px",
    display: "block",
  };

  return (
    <div
      style={{
        position: "relative",
        border: "1px solid var(--theme-border)",
        borderLeft: "4px solid #006657",
        borderRadius: "6px",
        marginBottom: "10px",
        padding: "12px 14px 10px",
        background: "var(--theme-surface)",
      }}
    >
      {/* Botón eliminar: siempre arriba a la derecha */}
      <div style={{ position: "absolute", top: "10px", right: "12px" }}>
        <CButton
          color="danger"
          variant="outline"
          size="sm"
          onClick={onDeleteClick}
        >
          <i className="fa fa-trash"></i>
        </CButton>
      </div>

      <CatalogItemPicker
        value={catalogItem}
        currency={currency}
        permissionNames={[CREDIT_NOTES_CREATE, CREDIT_NOTES_UPDATE]}
        onChange={onCatalogItemChange}
      />

      {/* Fila 1: Cantidad | Código | Precio Unitario */}
      <CRow className="align-items-end mb-2" style={{ paddingRight: "46px" }}>
        <CCol xs={4} md={1}>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Cant.
          </CLabel>
          <CInput
            type="number"
            step="0.001"
            value={emptyValueOnUndefined(editingItem.quantity)}
            onChange={onQuantityChange}
          ></CInput>
          <FieldErrors
            errors={getFieldErrors("quantity", errors) as string[]}
          ></FieldErrors>
        </CCol>
        <CCol xs={8} md={2}>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Código
          </CLabel>
          <CInput
            type="text"
            value={emptyValueOnUndefined(editingItem.code)}
            onChange={onCodeChange}
          ></CInput>
          <FieldErrors
            errors={getFieldErrors("code", errors) as string[]}
          ></FieldErrors>
        </CCol>
        <CCol xs={12} md={3}>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Precio Unitario
          </CLabel>
          <CurrencyField
            currency={currency}
            placeholder="Monto"
            onChange={onUnitAmountChange}
            value={editingItem.unitAmount}
            limit={1000000000000}
          />
          <FieldErrors
            errors={getFieldErrors("unitAmount", errors) as string[]}
          ></FieldErrors>
        </CCol>
      </CRow>

      {/* Fila 2: Detalle */}
      <CRow className="mb-2">
        <CCol>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Detalle
          </CLabel>
          <CTextarea
            placeholder="Descripción del ítem"
            value={emptyValueOnUndefined(editingItem.description)}
            onChange={onDescriptionChange}
            style={{ resize: "vertical", minHeight: "60px" }}
          ></CTextarea>
          <FieldErrors
            errors={getFieldErrors("description", errors) as string[]}
          ></FieldErrors>
        </CCol>
      </CRow>

      {/* Fila 3: Tipo IVA | % IVA */}
      <CRow className="align-items-end">
        <CCol xs={8} md={3}>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Tipo IVA
          </CLabel>
          <CSelect
            type="text"
            value={
              editingItem.ivaType ? editingItem.ivaType : IVA_TYPE_10_PERCENT
            }
            onChange={onIvaTypeChange}
          >
            <option value={""} disabled>
              -----
            </option>
            {Array.from(IVA_TYPE_CHOICES.entries()).map((entry) => {
              return (
                <option key={entry[0]} value={entry[0]}>
                  {entry[1]}
                </option>
              );
            })}
          </CSelect>
          <FieldErrors
            errors={getFieldErrors("ivaType", errors) as string[]}
          ></FieldErrors>
        </CCol>
        <CCol xs={4} md={2}>
          <CLabel style={itemLabelStyle}>% IVA</CLabel>
          <CInput
            type="number"
            onChange={onIvaBaseChange}
            placeholder="Porcentaje"
            disabled={
              editingItem.ivaType !== IVA_TYPE_MIXED_10_PERCENT &&
              editingItem.ivaType !== IVA_TYPE_MIXED_5_PERCENT
            }
            value={emptyValueOnUndefined(editingItem.ivaBase)}
          ></CInput>
          <FieldErrors
            errors={getFieldErrors("ivaBase", errors) as string[]}
          ></FieldErrors>
        </CCol>
        <CCol xs={12} md={4}>
          <CLabel style={itemLabelStyle}>Unidad de medida</CLabel>
          <MeasureUnitSelect
            value={editingItem.measureUnit ?? MEASURE_UNIT_UNIT}
            onChange={onMeasureUnitChange}
          />
          <FieldErrors
            errors={getFieldErrors("measureUnit", errors) as string[]}
          ></FieldErrors>
        </CCol>
      </CRow>

      {/* DNCP (solo para clientes gubernamentales) */}
      {client !== null && client.type === CLIENT_TYPE_GOVERNMENT && (
        <>
          <hr
            style={{
              margin: "10px 0",
              borderColor: "#f9b115",
              borderWidth: "2px",
            }}
          />
          <CRow className="align-items-end">
            <CCol xs={6} md={3}>
              <CLabel style={itemLabelStyle}>
                <span className="text-danger">*</span> Nivel DNCP General
              </CLabel>
              <CInput
                type="text"
                placeholder="Nivel general"
                onChange={onDncpLevelCodeGeneralChange}
                value={emptyValueOnUndefinedOrNull(
                  editingItem.dncpLevelCodeGeneral,
                )}
              ></CInput>
              <FieldErrors
                errors={
                  getFieldErrors("dncpLevelCodeGeneral", errors) as string[]
                }
              ></FieldErrors>
            </CCol>
            <CCol xs={6} md={3}>
              <CLabel style={itemLabelStyle}>
                <span className="text-danger">*</span> Nivel DNCP Específico
              </CLabel>
              <CInput
                type="text"
                placeholder="Nivel específico"
                onChange={onDncpLevelCodeSpecificChange}
                value={emptyValueOnUndefinedOrNull(
                  editingItem.dncpLevelCodeSpecific,
                )}
              ></CInput>
              <FieldErrors
                errors={
                  getFieldErrors("dncpLevelCodeSpecific", errors) as string[]
                }
              ></FieldErrors>
            </CCol>
          </CRow>
        </>
      )}
    </div>
  );
};

const propsAreEqual = (
  prevItemProps: CreditNoteItemFormProps,
  nextItemProps: CreditNoteItemFormProps,
): boolean => {
  return (
    prevItemProps.currency === nextItemProps.currency &&
    prevItemProps.client?.id === nextItemProps.client?.id &&
    prevItemProps.item.id === nextItemProps.item.id &&
    prevItemProps.item.code === nextItemProps.item.code &&
    prevItemProps.item.obs === nextItemProps.item.obs &&
    prevItemProps.item.measureUnit === nextItemProps.item.measureUnit &&
    prevItemProps.item.unitAmount === nextItemProps.item.unitAmount &&
    prevItemProps.item.description === nextItemProps.item.description &&
    prevItemProps.item.quantity === nextItemProps.item.quantity &&
    prevItemProps.item.totalAmount === nextItemProps.item.totalAmount &&
    prevItemProps.item.amount10Percent === nextItemProps.item.amount10Percent &&
    prevItemProps.item.amount5Percent === nextItemProps.item.amount5Percent &&
    prevItemProps.item.amountExempt === nextItemProps.item.amountExempt &&
    prevItemProps.item.ivaType === nextItemProps.item.ivaType &&
    prevItemProps.item.ivaBase === nextItemProps.item.ivaBase &&
    prevItemProps.item.iva10Percent === nextItemProps.item.iva10Percent &&
    prevItemProps.item.iva5Percent === nextItemProps.item.iva5Percent &&
    errorsAreSame(prevItemProps.errors, nextItemProps.errors)
  );
};

export default React.memo(CreditNoteItemForm, propsAreEqual);
